import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, Building2, MapPin, CreditCard, Save, Loader2, Search, Check, ArrowRight, Sun, Moon, DollarSign, Clock, AlertTriangle, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useThemeContext } from '@/contexts/ThemeContext';
import DateRangeFilter from '@/components/DateRangeFilter';

const PROVINCES = [
  'AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT',
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID',
  'IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO',
  'MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA',
  'RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

export default function ClientProfile() {
  const { token } = useAuth();
  const { theme, setTheme } = useThemeContext();
  const [profile, setProfile] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [invoiceDateStart, setInvoiceDateStart] = useState('');
  const [invoiceDateEnd, setInvoiceDateEnd] = useState('');
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', company_name: '', company_address: '',
    default_pickup_name: '', default_pickup_address: '', default_pickup_street: '',
    default_pickup_city: '', default_pickup_province: '', default_pickup_postal: '',
    default_pickup_unit: '', default_pickup_lat: null, default_pickup_lng: null,
  });
  const [suggestions, setSuggestions] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const debounceRef = useRef(null);

  useEffect(() => {
        api.get('/profile')
      .then(pRes => {
        setProfile(pRes.data);
        setForm({
          name: pRes.data.name || '',
          phone: pRes.data.phone || '',
          company_name: pRes.data.company_name || '',
          company_address: pRes.data.company_address || '',
          default_pickup_name: pRes.data.default_pickup_name || '',
          default_pickup_address: pRes.data.default_pickup_address || '',
          default_pickup_street: pRes.data.default_pickup_street || '',
          default_pickup_city: pRes.data.default_pickup_city || '',
          default_pickup_province: pRes.data.default_pickup_province || '',
          default_pickup_postal: pRes.data.default_pickup_postal || '',
          default_pickup_unit: pRes.data.default_pickup_unit || '',
          default_pickup_lat: pRes.data.default_pickup_lat || null,
          default_pickup_lng: pRes.data.default_pickup_lng || null,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  // Fetch invoices when payments tab is active
  useEffect(() => {
    if (activeTab !== 'payments') return;
    setInvoicesLoading(true);
    api.get('/invoices')
      .then(res => setInvoices(res.data || []))
      .catch(() => setInvoices([]))
      .finally(() => setInvoicesLoading(false));
  }, [activeTab]);

  const invoiceTotals = (() => {
    const totalBilled = invoices.reduce((s, inv) => s + (inv.total_amount || 0), 0);
    const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, inv) => s + (inv.total_amount || 0), 0);
    const outstanding = invoices.filter(i => i.status === 'sent').reduce((s, inv) => s + (inv.total_amount || 0), 0);
    const overdue = invoices.filter(i => i.status === 'overdue').reduce((s, inv) => s + (inv.total_amount || 0), 0);
    return { totalBilled, totalPaid, outstanding, overdue };
  })();

  const filteredInvoices = invoices.filter(inv => {
    if (invoiceFilter === 'due' && inv.status !== 'sent') return false;
    if (invoiceFilter === 'paid' && inv.status !== 'paid') return false;
    if (invoiceFilter === 'overdue' && inv.status !== 'overdue') return false;
    if (invoiceDateStart && inv.created_at < invoiceDateStart) return false;
    if (invoiceDateEnd && inv.created_at > invoiceDateEnd + 'T23:59:59') return false;
    return true;
  });

  const invoiceStatusBadge = (status) => {
    const styles = {
      draft: 'bg-muted text-muted-foreground border-border',
      sent: 'bg-blue-500/10 text-blue-600 border-blue-200',
      paid: 'bg-emerald-500/10 text-emerald-600 border-emerald-200',
      overdue: 'bg-red-500/10 text-red-600 border-red-200',
      cancelled: 'bg-muted text-muted-foreground border-border',
    };
    return styles[status] || styles.draft;
  };

  const geocode = useCallback((query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 3) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setGeoLoading(true);
      try {
        const res = await api.post('/routing/geocode', { address: query });
        setSuggestions(res.data || []);
      } catch { setSuggestions([]); }
      setGeoLoading(false);
    }, 400);
  }, [token]);

  const REGION_MAP = {'Alberta':'AB','British Columbia':'BC','Manitoba':'MB','New Brunswick':'NB','Newfoundland and Labrador':'NL','Nova Scotia':'NS','Ontario':'ON','Prince Edward Island':'PE','Quebec':'QC','Saskatchewan':'SK','Northwest Territories':'NT','Nunavut':'NU','Yukon':'YT','Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA','Colorado':'CO','Connecticut':'CT','Delaware':'DE','Florida':'FL','Georgia':'GA','Hawaii':'HI','Idaho':'ID','Illinois':'IL','Indiana':'IN','Iowa':'IA','Kansas':'KS','Kentucky':'KY','Louisiana':'LA','Maine':'ME','Maryland':'MD','Massachusetts':'MA','Michigan':'MI','Minnesota':'MN','Mississippi':'MS','Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV','New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK','Oregon':'OR','Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD','Tennessee':'TN','Texas':'TX','Utah':'UT','Vermont':'VT','Virginia':'VA','Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY','District of Columbia':'DC'};
  const selectSuggestion = (s) => {
    const parts = s.display_name.split(', ');
    let prov = '';
    for (const pt of parts) { if (REGION_MAP[pt.trim()]) { prov = REGION_MAP[pt.trim()]; break; } }
    if (!prov) prov = PROVINCES.find(p => parts.some(pt => pt.trim() === p)) || '';
    setForm(prev => ({
      ...prev,
      default_pickup_address: s.display_name,
      default_pickup_street: parts[0] || '',
      default_pickup_city: parts.length > 1 ? parts[0] : '',
      default_pickup_province: prov,
      default_pickup_lat: s.lat,
      default_pickup_lng: s.lng,
    }));
    setSuggestions([]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const fields = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== '' && v !== undefined) fields[k] = v;
      });
      const res = await api.put('/profile', fields);
      setProfile(prev => ({ ...prev, ...res.data }));
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="profile-loading">
        <Loader2 className="w-6 h-6 animate-spin text-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="client-profile">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Profile</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your account and preferences</p>
      </div>

      <div className="flex gap-6">
        {/* Vertical Side Menu */}
        <nav className="w-44 flex-shrink-0">
          <div className="space-y-1">
            {[
              { id: 'details', icon: User, label: 'Details' },
              { id: 'pickup', icon: MapPin, label: 'Default Pickup' },
              { id: 'payments', icon: CreditCard, label: 'Payments' },
              { id: 'appearance', icon: Sun, label: 'Appearance' },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                  activeTab === tab.id
                    ? 'bg-brand-accent/10 text-brand-accent border-l-2 border-brand-accent'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                data-testid={`profile-tab-${tab.id}`}>
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">

        {/* Details Tab */}
        <TabsContent value="details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="rounded-xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-bold tracking-wider text-muted-foreground flex items-center gap-2">
                  <User className="w-4 h-4" /> PERSONAL INFO
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-muted-foreground font-medium">Full Name</label>
                  <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Your name" className="rounded-xl h-11 mt-1" data-testid="profile-name" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-medium">Email</label>
                  <Input value={profile?.email || ''} disabled
                    className="rounded-xl h-11 mt-1 bg-muted/50 text-muted-foreground" data-testid="profile-email" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-medium">Phone</label>
                  <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                    placeholder="416-555-0100" className="rounded-xl h-11 mt-1" data-testid="profile-phone" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-medium">Role</label>
                  <div className="mt-1">
                    <Badge className="bg-brand-accent/10 text-foreground border-border text-xs">{profile?.role?.toUpperCase()}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-bold tracking-wider text-muted-foreground flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> COMPANY
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-muted-foreground font-medium">Company Name</label>
                  <Input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))}
                    placeholder="ACME Corp" className="rounded-xl h-11 mt-1" data-testid="profile-company-name" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-medium">Company Address</label>
                  <Input value={form.company_address} onChange={e => setForm(p => ({ ...p, company_address: e.target.value }))}
                    placeholder="100 King St W, Toronto, ON" className="rounded-xl h-11 mt-1" data-testid="profile-company-address" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-medium">Organization</label>
                  <Input value={profile?.organization?.name || ''} disabled
                    className="rounded-xl h-11 mt-1 bg-muted/50 text-muted-foreground" data-testid="profile-org-name" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-medium">Member Since</label>
                  <Input value={profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : ''} disabled
                    className="rounded-xl h-11 mt-1 bg-muted/50 text-muted-foreground" data-testid="profile-member-since" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={handleSave} disabled={saving}
              className="bg-brand-accent hover:bg-brand-accent/90 text-brand-accent-foreground font-bold rounded-lg px-6"
              data-testid="profile-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
            </Button>
          </div>
        </TabsContent>

        {/* Default Pickup Tab */}
        <TabsContent value="pickup">
          <Card className="rounded-xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold tracking-wider text-muted-foreground flex items-center gap-2">
                <MapPin className="w-4 h-4" /> DEFAULT PICKUP LOCATION
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Set your default pickup address to pre-fill new shipments</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground font-medium">Location Name</label>
                <Input value={form.default_pickup_name} onChange={e => setForm(p => ({ ...p, default_pickup_name: e.target.value }))}
                  placeholder="e.g. Main Warehouse, Toronto Hub" className="rounded-xl h-11 mt-1" data-testid="profile-pickup-name" />
              </div>
              <div className="relative">
                <label className="text-xs text-muted-foreground font-medium">Search Address</label>
                <div className="relative mt-1">
                  <Input
                    value={form.default_pickup_address}
                    onChange={e => { setForm(p => ({ ...p, default_pickup_address: e.target.value })); geocode(e.target.value); }}
                    placeholder="Start typing an address..."
                    className="rounded-xl h-11 pl-9"
                    data-testid="profile-pickup-search"
                  />
                  <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-3.5" />
                  {geoLoading && <Loader2 className="w-4 h-4 animate-spin text-foreground absolute right-3 top-3.5" />}
                </div>
                {suggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-muted border border-input rounded-xl shadow-lg max-h-64 overflow-y-auto">
                    {suggestions.map((s, i) => (
                      <button key={i} onClick={() => selectSuggestion(s)}
                        className="w-full text-left px-4 py-3 text-sm hover:bg-muted border-b border-border last:border-0"
                        data-testid={`pickup-suggestion-${i}`}>
                        <MapPin className="w-3.5 h-3.5 text-foreground inline mr-2 -mt-0.5" />
                        {s.display_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground font-medium">Street Address</label>
                  <Input value={form.default_pickup_street} onChange={e => setForm(p => ({ ...p, default_pickup_street: e.target.value }))}
                    placeholder="123 Main St" className="rounded-xl h-11 mt-1" data-testid="profile-pickup-street" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-medium">Unit / Suite #</label>
                  <Input value={form.default_pickup_unit || ''} onChange={e => setForm(p => ({ ...p, default_pickup_unit: e.target.value }))}
                    placeholder="Unit 4B" className="rounded-xl h-11 mt-1" data-testid="profile-pickup-unit" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-medium">City</label>
                  <Input value={form.default_pickup_city} onChange={e => setForm(p => ({ ...p, default_pickup_city: e.target.value }))}
                    placeholder="Toronto" className="rounded-xl h-11 mt-1" data-testid="profile-pickup-city" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-medium">Province</label>
                  <select value={form.default_pickup_province} onChange={e => setForm(p => ({ ...p, default_pickup_province: e.target.value }))}
                    className="w-full h-11 mt-1 rounded-xl border border-input px-3 text-sm bg-muted focus:outline-none focus:border-brand-accent"
                    data-testid="profile-pickup-province">
                    <option value="">Select...</option>
                    {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-medium">Postal Code</label>
                  <Input value={form.default_pickup_postal} onChange={e => setForm(p => ({ ...p, default_pickup_postal: e.target.value }))}
                    placeholder="M5V 2T6" className="rounded-xl h-11 mt-1" data-testid="profile-pickup-postal" />
                </div>
              </div>
              {form.default_pickup_lat && (
                <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2">
                  <Check className="w-3.5 h-3.5" /> Location verified ({form.default_pickup_lat.toFixed(4)}, {form.default_pickup_lng.toFixed(4)})
                </div>
              )}
            </CardContent>
          </Card>

          <div className="mt-6 flex justify-end">
            <Button onClick={handleSave} disabled={saving}
              className="bg-brand-accent hover:bg-brand-accent/90 text-brand-accent-foreground font-bold rounded-lg px-6"
              data-testid="pickup-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Default Pickup</>}
            </Button>
          </div>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          {invoicesLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-foreground" />
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card className="rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-brand-accent/10 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-brand-accent" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-foreground" data-testid="payments-total-billed">
                          ${invoiceTotals.totalBilled.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs text-muted-foreground font-bold tracking-wider">TOTAL BILLED</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <Check className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-emerald-600" data-testid="payments-total-paid">
                          ${invoiceTotals.totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs text-muted-foreground font-bold tracking-wider">TOTAL PAID</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600" data-testid="payments-outstanding">
                          ${invoiceTotals.outstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs text-muted-foreground font-bold tracking-wider">OUTSTANDING</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-red-600" data-testid="payments-overdue">
                          ${invoiceTotals.overdue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs text-muted-foreground font-bold tracking-wider">OVERDUE</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Date Range Filter */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <DateRangeFilter
                  dateStart={invoiceDateStart}
                  dateEnd={invoiceDateEnd}
                  onStartChange={setInvoiceDateStart}
                  onEndChange={setInvoiceDateEnd}
                  onClear={() => { setInvoiceDateStart(''); setInvoiceDateEnd(''); }}
                />
              </div>

              {/* Status Tabs */}
              <div className="flex gap-1 mb-4 bg-muted/50 rounded-lg p-1 w-fit">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'due', label: 'Due' },
                  { id: 'paid', label: 'Paid' },
                  { id: 'overdue', label: 'Overdue' },
                ].map(tab => (
                  <button key={tab.id} onClick={() => setInvoiceFilter(tab.id)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      invoiceFilter === tab.id
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`invoice-filter-${tab.id}`}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Invoice List */}
              <Card className="rounded-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold tracking-wider text-muted-foreground flex items-center gap-2">
                    <FileText className="w-4 h-4" /> INVOICES
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {filteredInvoices.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground" data-testid="no-invoices">
                      <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm font-medium">No invoices found</p>
                      <p className="text-xs mt-1">Invoices will appear here once loads are billed</p>
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="invoices-list">
                      {filteredInvoices.map(inv => (
                        <div key={inv.id} className="border border-border rounded-xl overflow-hidden">
                          <button
                            onClick={() => setExpandedInvoice(expandedInvoice === inv.id ? null : inv.id)}
                            className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors text-left"
                            data-testid={`invoice-row-${inv.id}`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 rounded-xl bg-brand-accent/10 flex items-center justify-center shrink-0">
                                <FileText className="w-5 h-5 text-foreground" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold truncate">
                                  INV-{(inv.id || '').slice(-8).toUpperCase()}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                  <span>{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '-'}</span>
                                  {inv.load_id && (
                                    <>
                                      <span className="text-border">|</span>
                                      <span>Load: {inv.load_id.slice(-8).toUpperCase()}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <div className="text-right">
                                <p className="text-sm font-bold text-foreground">
                                  ${(inv.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                              </div>
                              <Badge className={`text-[10px] ${invoiceStatusBadge(inv.status)}`}>
                                {(inv.status || 'draft').toUpperCase()}
                              </Badge>
                              {expandedInvoice === inv.id ? (
                                <ChevronUp className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              )}
                            </div>
                          </button>

                          {/* Expanded Detail */}
                          {expandedInvoice === inv.id && (
                            <div className="border-t border-border bg-muted/30 p-4 space-y-3" data-testid={`invoice-detail-${inv.id}`}>
                              {inv.cargo_description && (
                                <div>
                                  <p className="text-xs text-muted-foreground font-medium mb-1">Cargo</p>
                                  <p className="text-sm">{inv.cargo_description}</p>
                                </div>
                              )}
                              {(inv.pickup_address || inv.delivery_address) && (
                                <div>
                                  <p className="text-xs text-muted-foreground font-medium mb-1">Route</p>
                                  <div className="flex items-center text-sm">
                                    <MapPin className="w-3.5 h-3.5 mr-1 text-muted-foreground shrink-0" />
                                    <span className="truncate">{inv.pickup_address || 'N/A'}</span>
                                    <ArrowRight className="w-3.5 h-3.5 mx-2 text-muted-foreground shrink-0" />
                                    <span className="truncate">{inv.delivery_address || 'N/A'}</span>
                                  </div>
                                </div>
                              )}
                              {inv.charges && inv.charges.length > 0 && (
                                <div>
                                  <p className="text-xs text-muted-foreground font-medium mb-1">Charges</p>
                                  <div className="space-y-1">
                                    {inv.charges.map((charge, idx) => (
                                      <div key={idx} className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">{charge.description || charge.type}</span>
                                        <span className="font-medium">${(charge.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance">
          <Card className="rounded-xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold tracking-wider text-muted-foreground flex items-center gap-2">
                <Sun className="w-4 h-4" /> APPEARANCE
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Choose your preferred theme</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <button onClick={() => setTheme('light')}
                  className={`p-6 rounded-xl border-2 text-center transition-all ${
                    theme === 'light' ? 'border-brand-accent bg-brand-accent/10 ring-2 ring-brand-accent' : 'border-border hover:border-muted-foreground'
                  }`}>
                  <Sun className="w-8 h-8 mx-auto mb-3 text-amber-500" />
                  <p className="font-bold">Light Mode</p>
                  <p className="text-xs text-muted-foreground mt-1">Clean, bright interface</p>
                </button>
                <button onClick={() => setTheme('dark')}
                  className={`p-6 rounded-xl border-2 text-center transition-all ${
                    theme === 'dark' ? 'border-brand-accent bg-brand-accent/10 ring-2 ring-brand-accent' : 'border-border hover:border-muted-foreground'
                  }`}>
                  <Moon className="w-8 h-8 mx-auto mb-3 text-indigo-400" />
                  <p className="font-bold">Dark Mode</p>
                  <p className="text-xs text-muted-foreground mt-1">Easy on the eyes</p>
                </button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
        </div>{/* end content */}
      </div>{/* end flex */}
    </div>
  );
}
